/* global Klarna */
/* eslint-disable react/forbid-component-props, no-console,react-hooks/exhaustive-deps, sonarjs/cognitive-complexity, complexity */
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Field, Formik } from 'formik';
import upperCase from 'lodash/upperCase';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LOCALE } from '../../../brands/default/config';
import {
  PAYMENT_TOP_CTA_ID,
  PAYMENT_DOD_TRACKING_GATEWAY,
} from '../../../brands/default/constants';
import {
  BUTTON_PLACE_ORDER,
  KLARNA_DISABLED_HOVER_MESSAGE,
  KLARNA_DISABLED_MESSAGE,
  DEFAULT_PAYMENT_ERROR,
  ERROR_MESSAGE_KLARNA,
  VIP_PAY_BUTTON_MESSAGE,
  VIP_PAY_BUTTON_MESSAGE_EXCLUSIVE,
  VIP_PAY_BUTTON_MESSAGE_SELECTED,
  VIP_PAY_EXCLUSIVE_DEAL_MESSAGE,
} from '../../../brands/default/content/components/checkout';
import {
  trackEvent,
  trackPurchase,
  updateDod,
} from '../../../helpers/analytics';
import { prepareTrackingData } from '../../../helpers/general';
import {
  createClientSession,
  preparePaymentData,
  sendKlarnaPayment,
  setAttachment,
  setOrderlines,
  updateClientSession,
} from '../../../helpers/klarna';
import Message, { MESSAGE_TYPES } from '../../_generic/Message';
import CTAButton from '../../_generic/button/CTAButton';
import ClientOnlyPortal from '../../_generic/portal/ClientOnlyPortal';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import ButtonGoToPayment from './ButtonGoToPayment';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentSummary from './PaymentSummary';
import VipMessageWrapper from './VipMessageWrapper';
import VipToggleButtons from './VipToggleButtons';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentKlarna = ({
  afterError,
  atolProtected,
  basketProducts,
  currencySymbol,
  extraMerchantData,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isModernVip = false,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  payFinalOrderAmount,
  promoCode,
  setVipOptIn,
  totalQuantity,
  travelDisableButton,
  vipOneYearPrice,
  vipOptIn = null,
  vipOptInFinalOrderAmount,
  vipOptInPrice,
  vipOptInType,
  vipPreButtonMessage,
  vipSubscriptionMessage,
  vipSummary,
  welcomeVipEnabled,
  basketDiscountForExistingVipUser,
}) => {
  const formikRef = React.useRef(null);
  const toast = useToast();
  const container = '#klarna-payments-container';
  const [isLoading, setIsLoading] = useState(false);
  const [paymentInProcess, setPaymentInProcess] = useState(false);
  const [klarnaSession, SetKlarnaSession] = useState(null);
  const router = useRouter();
  const userProfile = useSelector((state) => state.user.userprofile);
  const [accountIncomplete, setAccountIncomplete] = useState(false);
  const isAccountCompleted = Boolean(
    userProfile.postcode &&
      userProfile.addressLine1 &&
      userProfile.town &&
      userProfile.firstName &&
      userProfile.lastName,
  );
  useEffect(() => {
    setAccountIncomplete(!isAccountCompleted);
  }, [isAccountCompleted, userProfile]);

  const handleVipToggleClick = (vipOptInValue) => {
    setVipOptIn(vipOptInValue);
  };

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const sendPayment = async (authorizationToken) => {
    const paymentData = await preparePaymentData({
      authorizationToken,
      basketDiscountForExistingVipUser,
      basketProducts,
      isWalletApplied,
      orderSummary,
      payFinalOrderAmount,
      promoCode,
      sessionId: klarnaSession,
      vipOptIn,
      vipOptInType,
      vipSummary,
    });

    if (router.query.klarnaTest === 'true') {
      console.log(paymentData);

      return;
    }
    const order = await sendKlarnaPayment(paymentData);

    if (order) {
      const purchaseOrder = prepareTrackingData({
        basketProducts,
        order,
        orderSummary,
        paymentData,
        type: 'Klarna',
      });
      trackEvent('klarna_successful');
      localStorage.removeItem('klarnaInfo');
      trackPurchase(basketProducts, purchaseOrder, false);
      orderComplete(order);
    } else {
      trackEvent('klarna_failed');
      notifyError(ERROR_MESSAGE_KLARNA);
    }
    setPaymentInProcess(false);
  };

  const authorizePayment = async () => {
    trackEvent('klarna_click');
    if (welcomeVipEnabled && vipOptIn) trackEvent('vip_klarnabw_click');
    if (welcomeVipEnabled && !vipOptIn) trackEvent('vip_klarnabwo_click');
    updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.KLARNA });
    setPaymentInProcess(true);
    const orderLines = setOrderlines({
      isVipOptIn: vipOptIn,
      orderSummary,
      products: basketProducts,
      vipPrice: welcomeVipEnabled ? vipOptInPrice : vipOneYearPrice,
      vipSummary,
    });
    const attachment = setAttachment({
      extraMerchantData,
      products: basketProducts,
    });
    const data = {
      attachment,
      locale: LOCALE,
      order_amount: Math.round(payFinalOrderAmount * 100),
      order_lines: orderLines,
      purchase_country: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
      purchase_currency: upperCase(orderSummary?.currency),
    };
    try {
      const validSession = await updateClientSession({
        consent: true,
        data,
        sessionId: klarnaSession,
      });

      if (!validSession) {
        notifyError(DEFAULT_PAYMENT_ERROR);
        setPaymentInProcess(false);

        return;
      }

      Klarna.Payments.authorize(
        {
          payment_method_category: 'pay_over_time',
        },
        {
          locale: LOCALE,
          order_amount: Math.round(payFinalOrderAmount * 100),
          order_lines: orderLines,
          purchase_country: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
          purchase_currency: upperCase(orderSummary?.currency),
        },
        (response) => {
          if (response?.authorization_token) {
            sendPayment(response);
          } else if (!response?.approved && response.show_form) {
            notifyError(ERROR_MESSAGE_KLARNA);
            trackEvent('klarna_aborted');
            setPaymentInProcess(false);
          } else if (!response?.approved && !response.show_form) {
            notifyError(ERROR_MESSAGE_KLARNA);
            trackEvent('klarna_failed');
            localStorage.removeItem('klarnaInfo');
            setPaymentInProcess(false);
          }
        },
      );
    } catch {
      notifyError(ERROR_MESSAGE_KLARNA);
      trackEvent('klarna_failed');
      localStorage.removeItem('klarnaInfo');
      setPaymentInProcess(false);
    }
  };

  const createSession = async (data) => {
    try {
      const clientSession = await createClientSession(data);
      if (clientSession) {
        SetKlarnaSession(clientSession.session_id);
        const klarnaInfo = {
          clientToken: clientSession.client_token,
          sessionId: clientSession.session_id,
        };
        localStorage.setItem('klarnaInfo', JSON.stringify(klarnaInfo));

        return clientSession.client_token;
      }

      return null;
    } catch {
      notifyError(ERROR_MESSAGE_KLARNA);
      setPaymentInProcess(false);

      return null;
    }
  };

  const initializeKlarnaSDK = async () => {
    if (!payFinalOrderAmount) return;
    const orderLines = setOrderlines({
      isVipOptIn: vipOptIn,
      orderSummary,
      products: basketProducts,
      vipPrice: welcomeVipEnabled ? vipOptInPrice : vipOneYearPrice,
      vipSummary,
    });
    const attachment = setAttachment({
      extraMerchantData,
      products: basketProducts,
    });
    const klarnaInfo = JSON.parse(localStorage.getItem('klarnaInfo'));
    const sessionId = klarnaInfo?.sessionId;
    let clientToken = klarnaInfo?.clientToken;
    const data = {
      attachment,
      locale: LOCALE,
      order_amount: Math.round(payFinalOrderAmount * 100),
      order_lines: orderLines,
      purchase_country: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE),
      purchase_currency: upperCase(orderSummary?.currency),
    };
    if (sessionId) {
      const validSession = await updateClientSession({ data, sessionId });
      SetKlarnaSession(sessionId);
      if (!validSession) {
        clientToken = await createSession(data);
      }
    } else {
      clientToken = await createSession(data);
    }
    try {
      if (clientToken) {
        Klarna.Payments.init({
          client_token: clientToken,
        });
        Klarna.Payments.load({
          container,
          payment_method_category: 'pay_over_time',
        });
      }
    } catch {
      afterError();
    }

    toggleLoaderOff();
  };

  useEffect(() => {
    toggleLoaderOn();
    initializeKlarnaSDK();
    formikRef.current.validateForm();
  }, [payFinalOrderAmount, vipOptIn]);

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      {accountIncomplete && (
        <Message
          extraClasses="login-to-purchase"
          hoverMessage={KLARNA_DISABLED_HOVER_MESSAGE}
          message={KLARNA_DISABLED_MESSAGE}
        />
      )}
      <Formik
        enableReinitialize
        initialValues={{
          cardCvv: '',
          confirmPayment: true,
          isVipOptedIn: vipOptIn,
        }}
        innerRef={formikRef}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }
          if (
            !isVipUser &&
            welcomeVipEnabled &&
            vipOptIn === null &&
            !isModernVip
          ) {
            errors.isVipOptedIn = true;
          }

          return errors;
        }}
      >
        {({ handleSubmit, isValid, isSubmitting }) => (
          <form className="" name="payment" onSubmit={handleSubmit}>
            <div className="row payment-body">
              <div className="col-12">
                <div id="klarna-payments-container" />
                <PaymentAgreeTerms disabled={false} />
                <BasketSocialCues
                  atolProtected={atolProtected}
                  className="with-margin"
                  isLighter
                />
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  totalQuantity={totalQuantity}
                />
              </div>
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
              {!isVipUser && welcomeVipEnabled && (
                <>
                  <VipToggleButtons
                    currencySymbol={currencySymbol}
                    finalOrderAmount={finalOrderAmount}
                    isExclusiveProductInBasket={isExclusiveProductInBasket}
                    setPayButton={handleVipToggleClick}
                    vipOptInFinalOrderAmount={vipOptInFinalOrderAmount}
                    vipSubscriptionMessage={vipSubscriptionMessage}
                  />
                  <Field
                    id="isVipOptedIn"
                    name="isVipOptedIn"
                    type="hidden"
                    value={vipOptIn || ''}
                  />
                  <div className="vip-messages">
                    {isExclusiveProductInBasket && (
                      <div className="vip-pay-button-message">
                        <Message message={VIP_PAY_EXCLUSIVE_DEAL_MESSAGE} />
                      </div>
                    )}
                    {isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={VIP_PAY_BUTTON_MESSAGE_EXCLUSIVE}
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {!isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={VIP_PAY_BUTTON_MESSAGE}
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {vipOptIn !== null && (
                      <div className="vip-pay-button-message">
                        {VIP_PAY_BUTTON_MESSAGE_SELECTED}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12">
                <div
                  className={classNames({
                    'vip-pay-button-container': !isVipUser && welcomeVipEnabled,
                  })}
                >
                  <CTAButton
                    className="full-width buy-with-vip-button"
                    data-qa="payWithKlarna"
                    disabled={
                      travelDisableButton ||
                      paymentInProcess ||
                      isSubmitting ||
                      !isValid ||
                      accountIncomplete
                    }
                    onClick={() => authorizePayment()}
                    type="submit"
                  >
                    <FontAwesomeIcon
                      icon={faLock}
                      style={{ height: 13, marginRight: '4px', width: 13 }}
                    />
                    {BUTTON_PLACE_ORDER}
                  </CTAButton>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
      <ClientOnlyPortal targetElementId={PAYMENT_TOP_CTA_ID}>
        <ButtonGoToPayment />
      </ClientOnlyPortal>
      <style jsx>{`
        .disabled-overlay {
          position: relative;
          margin-bottom: -8px;
        }
        .disabled-overlay::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: grey;
          opacity: 0.3;
          z-index: 1000;
        }
        .vip-pay-button-container {
          margin: 0 auto 10px auto;
          width: 100%;
        }
        .vip-messages {
          margin: 0 auto;
          padding: 0 10px;
        }
        .vip-pay-button-message {
          margin: 0 auto 10px auto;
          text-align: center;
        }
      `}</style>
    </>
  );
};

PaymentKlarna.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketDiscountForExistingVipUser: PropTypes.number,
  basketProducts: PropTypes.array.isRequired,
  currencySymbol: PropTypes.string,
  extraMerchantData: PropTypes.object.isRequired,
  finalOrderAmount: PropTypes.number,
  isExclusiveProductInBasket: PropTypes.bool,
  isModernVip: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  payFinalOrderAmount: PropTypes.number,
  promoCode: PropTypes.object,
  setVipOptIn: PropTypes.func,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOneYearPrice: PropTypes.number,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipOptInPrice: PropTypes.number,
  vipOptInType: PropTypes.string,
  vipPreButtonMessage: PropTypes.element,
  vipSubscriptionMessage: PropTypes.element,
  vipSummary: PropTypes.object,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentKlarna;
